import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { fmtCurrency, fmtPct } from "../../lib/format";

const META = {
  net_income: { title: "Net Income", desc: "Location profit after its share of corporate expenses." },
  total_revenue: { title: "Total Revenue", desc: "Revenue mix and contribution by location." },
  total_expenses: { title: "Total Expenses", desc: "Operating costs by category, and corporate allocation." },
  net_ordinary_income: { title: "Net Ordinary Income", desc: "Location profit before the corporate allocation." },
  occupancy: { title: "Occupancy", desc: "Seat utilisation and mix across the portfolio." },
};

function Row({ label, value, pct, negative, bold, bar }) {
  return (
    <div className={`flex items-center justify-between py-2.5 border-b border-brand-border last:border-0 ${bold ? "font-medium" : ""}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${bold ? "text-brand-ink" : "text-brand-muted"} truncate`}>{label}</p>
        {bar != null && (
          <div className="mt-1.5 h-1 bg-brand-hover rounded-full overflow-hidden">
            <div className="h-full bg-brand-primary rounded-full" style={{ width: `${Math.max(2, Math.min(100, bar))}%` }} />
          </div>
        )}
      </div>
      <div className="text-right pl-4 shrink-0">
        <p className={`font-mono text-sm ${negative ? "text-brand-neg" : "text-brand-ink"}`}>{value}</p>
        {pct != null && <p className="text-[11px] text-brand-tertiary">{pct}</p>}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 first:mt-2">
      <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1">{title}</p>
      {children}
    </div>
  );
}

export default function KpiDrillDown({ metric, summary, locBreakdown, periodLabel, onClose }) {
  const open = !!metric;
  const t = summary?.totals || {};
  const rev = t.total_revenue || 0;
  const meta = META[metric] || {};
  const locs = (locBreakdown || []).filter((l) => !l.is_corporate);

  const byLocation = (field, { pctOfRevenue, margin } = {}) => {
    const rows = [...locs].sort((a, b) => (b[field] || 0) - (a[field] || 0));
    const max = Math.max(1, ...rows.map((r) => Math.abs(r[field] || 0)));
    return rows.map((r) => (
      <Row key={r.location} label={r.location} value={fmtCurrency(r[field])}
        negative={(r[field] || 0) < 0}
        bar={(Math.abs(r[field] || 0) / max) * 100}
        pct={margin ? `${fmtPct(r.total_revenue ? r.net_ordinary_income / r.total_revenue : null)} margin`
          : pctOfRevenue && rev ? `${((r[field] / rev) * 100).toFixed(1)}% of rev` : null} />
    ));
  };

  const hasLocs = locs.length > 0;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:w-[520px] bg-brand-page border-brand-border overflow-y-auto" data-testid="kpi-drilldown">
        <SheetHeader className="text-left">
          <SheetTitle className="font-serif text-3xl font-light text-brand-ink">{meta.title}</SheetTitle>
          <SheetDescription className="text-sm text-brand-muted">{meta.desc} · {periodLabel}</SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {metric === "net_income" && (summary.consolidated ? (
            <>
              <Section title="How we get to Net Income">
                <Row label="Location Net Income" value={fmtCurrency(t.location_net_income)} bold />
                <Row label="Management Fee" value={`+ ${fmtCurrency(t.management_fee)}`} />
                <Row label="Net Income" value={fmtCurrency(t.net_income)} bold />
              </Section>
              <p className="text-[11px] text-brand-tertiary mt-2">Each location's net income is already after its corporate expense allocation. The management fee (from corporate management contracts) is added on top.</p>
              {hasLocs && <Section title="Net Income by location">{byLocation("net_income", { pctOfRevenue: false })}</Section>}
            </>
          ) : (
            <Section title="How we get to Net Income">
              <Row label="Net Ordinary Income" value={fmtCurrency(t.net_ordinary_income)} />
              <Row label="Legal / Bad Debt" value={`(${fmtCurrency(t.legal_bad_debt)})`} negative />
              <Row label="Corporate Expense Allocation" value={`(${fmtCurrency(t.corp_expense_allocation)})`} negative />
              <Row label="Net Income" value={fmtCurrency(t.net_income)} bold />
            </Section>
          ))}

          {metric === "total_revenue" && (
            <>
              <Section title="Revenue by type">
                {(summary.revenue_breakdown || []).filter((r) => r.value).map((r) => (
                  <Row key={r.key} label={r.label} value={fmtCurrency(r.value)}
                    bar={rev ? (r.value / rev) * 100 : 0} pct={rev ? `${((r.value / rev) * 100).toFixed(1)}%` : null} />
                ))}
                <Row label="Total Revenue" value={fmtCurrency(rev)} bold />
              </Section>
              {hasLocs && <Section title="Revenue by location">{byLocation("total_revenue", { pctOfRevenue: true })}</Section>}
            </>
          )}

          {metric === "total_expenses" && (() => {
            const items = [...(summary.expense_breakdown || [])];
            if (t.corp_expense_allocation) items.push({ key: "corp", label: "Corporate Allocation", value: t.corp_expense_allocation });
            const totalExp = t.total_expenses || 0;
            return (
              <>
                <Section title="Expenses by category (% of revenue)">
                  {items.filter((i) => i.value).sort((a, b) => b.value - a.value).map((i) => (
                    <Row key={i.key} label={i.label} value={fmtCurrency(i.value)}
                      bar={rev ? (i.value / rev) * 100 : 0} pct={rev ? `${((i.value / rev) * 100).toFixed(1)}% of rev` : null} />
                  ))}
                  <Row label="Total Operating Expenses" value={fmtCurrency(totalExp)} bold />
                </Section>
              </>
            );
          })()}

          {metric === "net_ordinary_income" && (
            <>
              <Section title="Portfolio">
                <Row label="Total Revenue" value={fmtCurrency(rev)} />
                <Row label="Total Expenses" value={`(${fmtCurrency(t.total_expenses)})`} negative />
                <Row label="Net Ordinary Income" value={fmtCurrency(t.net_ordinary_income)} bold />
              </Section>
              {hasLocs && <Section title="By location (with margin)">{byLocation("net_ordinary_income", { margin: true })}</Section>}
            </>
          )}

          {metric === "occupancy" && (() => {
            const po = t.private_office || 0, dd = t.dedicated_desk || 0, cw = t.coworking_desk || 0;
            const cap = t.total_seats_capacity || 0, occ = t.total_seats_occupied || 0;
            const mix = [["Private Office", po], ["Dedicated Desk", dd], ["Coworking Desk", cw]];
            const mixTotal = po + dd + cw || 1;
            return (
              <>
                <Section title="Seats">
                  <Row label="Occupied Seats" value={num(occ)} />
                  <Row label="Total Capacity" value={num(cap)} />
                  <Row label="Occupancy" value={fmtPct(t.occupancy_pct)} bold />
                </Section>
                <Section title="Seat mix (occupied)">
                  {mix.map(([label, v]) => (
                    <Row key={label} label={label} value={num(v)} bar={(v / mixTotal) * 100} pct={`${((v / mixTotal) * 100).toFixed(1)}%`} />
                  ))}
                </Section>
                {hasLocs && (
                  <Section title="Occupancy by location">
                    {[...locs].sort((a, b) => (b.occupancy_pct || 0) - (a.occupancy_pct || 0)).map((r) => (
                      <Row key={r.location} label={r.location} value={fmtPct(r.occupancy_pct)} bar={(r.occupancy_pct || 0) * 100} />
                    ))}
                  </Section>
                )}
              </>
            );
          })()}

          {!hasLocs && metric !== "total_expenses" && metric !== "occupancy" && (
            <p className="mt-6 text-xs text-brand-tertiary">Switch the Location filter to "All Locations" to see the by-location breakdown.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function num(n) {
  return n == null ? "—" : Math.round(Number(n)).toLocaleString();
}
